/* ============================================================
   storage — search: every file you can see, in one flat list.

   It was its own section ("All files"); it is now what the search
   box opens — a query, or no query and the filters, which is
   "everything" — and what Home's "see all" opens, newest first.
   Across every drive by default, or one.

   The question a folder tree cannot answer: not just "what is
   biggest", but "where are the videos", "what did teg add",
   "what is the oldest thing still here". Browsing for any of
   those means opening folders one at a time and holding the
   answer in your head.

   This began as a largest-files list, which is one sort of one
   column — a useful answer to a single question and no help
   with the rest. The flattening was always the valuable part;
   the ordering is just the last step, so the sort is now a
   control rather than the identity of the page.

   The whole list comes down in one request and is sorted in the
   browser. That is the right trade at this size — a few thousand
   rows is nothing to sort, and it makes re-sorting instant and
   every filter free. If a drive ever holds a hundred thousand
   files, this is the thing to move server-side.
   ============================================================ */

import React, { useEffect, useMemo, useState } from 'react';
import { Icon, MarkerBadge, fileIconName, fmtBytes, fmtDate, parentOf } from './lib';
import { api } from './api';

/* Sorting is (column, direction) rather than a list of named orders, because
   the same five columns each want both directions and enumerating ten of them
   is how "Largest first / Smallest first" ended up as two entries that could
   not be reached by clicking the size header. */
const COLUMNS = {
  name: { label: 'Name', get: (f) => (f.original_name || '').toLowerCase(), text: true },
  where: { label: 'Folder', get: (f) => (f.path || '').toLowerCase(), text: true },
  owner: { label: 'Added by', get: (f) => (f.owner_name || '').toLowerCase(), text: true },
  size: { label: 'Size', get: (f) => f.size || 0 },
  added: { label: 'Added', get: (f) => f.created_at || 0 },
};

/* The groups people actually look for. Derived from the same helper that picks
   the row icon, so a file can never be filed under one thing and drawn as
   another. */
const KINDS = {
  video: { label: 'Video', icons: ['video'] },
  image: { label: 'Images', icons: ['image'] },
  audio: { label: 'Audio', icons: ['audio'] },
  pdf: { label: 'PDFs', icons: ['pdf'] },
  archive: { label: 'Archives', icons: ['archive'] },
  code: { label: 'Code', icons: ['code'] },
  other: { label: 'Other', icons: ['file'] },
};

const kindOf = (f) => {
  const icon = fileIconName(f.mime_type, f.original_name);
  const hit = Object.entries(KINDS).find(([, v]) => v.icons.includes(icon));
  return hit ? hit[0] : 'other';
};

const PAGE = 100;

export default function AllFiles({
  token, drives, me, onOpenFolder, onOpenFile, onDownload, previewId,
  query: urlQuery = '', onQuery, initialSort = 'added', initialOwner = '',
}) {
  const [drive, setDrive] = useState('all');         // 'all' = every drive you may browse
  const [files, setFiles] = useState(null);
  const [error, setError] = useState('');
  const [sort, setSort] = useState(initialSort);
  const [desc, setDesc] = useState(!COLUMNS[initialSort]?.text);
  const [query, setQueryState] = useState(urlQuery);
  const [kind, setKind] = useState('');            // '' = every kind
  const [owner, setOwner] = useState(initialOwner); // '' = everyone
  const [shown, setShown] = useState(PAGE);
  // The query lives in the address too (?view=search&q=...), so a search is a link.
  const setQuery = (v) => { setQueryState(v); onQuery?.(v); };
  useEffect(() => { setQueryState(urlQuery); }, [urlQuery]);
  const browsable = drives.filter((d) => d.online && d.perms?.browse !== false);
  const browsableKey = browsable.map((d) => d.id).join(',');

  /* A useState initialiser runs once, so picking the drive there picks it from
     whatever `drives` happened to be at mount — and if that was the empty array
     the app starts with, nothing ever selects one and the view sits on its
     skeletons forever. Choosing in an effect also covers a drive that goes
     away while you are looking at it. */
  useEffect(() => {
    if (drive !== 'all' && drives.length && !drives.some((d) => d.id === drive)) setDrive('all');
  }, [drives, drive]);

  useEffect(() => {
    const ids = drive === 'all' ? browsableKey.split(',').filter(Boolean) : [drive];
    if (!ids.length) { if (drives.length) setFiles([]); return undefined; }
    const ctrl = new AbortController();
    setFiles(null); setError(''); setShown(PAGE);
    Promise.all(ids.map((id) => api.allFiles(token, id, ctrl.signal)
      .then((d) => (Array.isArray(d) ? d : (d.files || [])))
      .catch((e) => { if (e.name === 'AbortError' || ids.length === 1) throw e; return []; })))
      .then((lists) => setFiles(lists.flat()))
      .catch((e) => { if (e.name !== 'AbortError') setError(e.message || 'Could not read this drive'); });
    return () => ctrl.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, drive, browsableKey]);

  // Counts come from everything on the drive, not from what survives the other
  // filters — a chip that reads "Video 0" because you have typed a name in the
  // search box is telling you about your own typing, not about the drive.
  const kindCounts = useMemo(() => {
    const out = {};
    for (const f of files || []) out[kindOf(f)] = (out[kindOf(f)] || 0) + 1;
    return out;
  }, [files]);

  const owners = useMemo(() => {
    const by = new Map();
    for (const f of files || []) {
      const id = f.owner_id;
      const cur = by.get(id) || { id, name: f.owner_name || '—', bytes: 0, count: 0 };
      cur.bytes += f.size || 0; cur.count += 1;
      by.set(id, cur);
    }
    return [...by.values()].sort((a, b) => b.bytes - a.bytes);
  }, [files]);

  const rows = useMemo(() => {
    if (!files) return [];
    const q = query.trim().toLowerCase();
    const col = COLUMNS[sort];
    const dir = desc ? -1 : 1;
    return files
      .filter((f) => (!owner || f.owner_id === owner)
        && (!kind || kindOf(f) === kind)
        && (!q || f.original_name.toLowerCase().includes(q) || (f.path || '').toLowerCase().includes(q)))
      .sort((a, b) => {
        const x = col.get(a); const y = col.get(b);
        if (col.text) return dir * String(x).localeCompare(String(y), undefined, { numeric: true });
        return dir * (x - y);
      });
  }, [files, sort, desc, query, kind, owner]);

  const total = rows.reduce((n, f) => n + (f.size || 0), 0);
  const driveRow = drives.find((d) => d.id === drive);
  const label = (id) => drives.find((d) => d.id === id)?.label || id;
  const filtered = files && rows.length !== files.length;

  // Clicking a column you are already sorted by reverses it; clicking a new one
  // starts in the direction that column is usually wanted in — biggest and
  // newest first, but names from A.
  const sortBy = (key) => {
    if (key === sort) { setDesc((v) => !v); return; }
    setSort(key);
    setDesc(!COLUMNS[key].text);
    setShown(PAGE);
  };

  const header = (key) => (
    <button type="button" className={`st-all-h ${sort === key ? 'is-on' : ''}`}
            onClick={() => sortBy(key)}
            aria-label={`Sort by ${COLUMNS[key].label}`}>
      {COLUMNS[key].label}
      {/* One chevron in the set, pointing right; the direction is a rotation
          rather than two more icons to keep in step with each other. */}
      {sort === key && (
        <Icon name="chevron" size={12}
              className={`st-all-caret ${desc ? 'is-desc' : 'is-asc'}`}/>
      )}
    </button>
  );

  const reset = () => { setQuery(''); setKind(''); setOwner(''); setShown(PAGE); };

  return (
    <div className="st-dash">
      <div className="st-dash-head">
        <h2 className="h2">Search</h2>
        <span className="meta">
          {files === null ? 'reading…'
            : `${rows.length.toLocaleString()} file${rows.length === 1 ? '' : 's'} · ${fmtBytes(total)}`
              + (filtered ? ` of ${files.length.toLocaleString()}` : '')}
        </span>
      </div>

      <div className="st-all-tools">
        <div className="st-all-drives">
          <button type="button" className={`st-tab ${drive === 'all' ? 'is-on' : ''}`}
                  onClick={() => setDrive('all')}>All drives</button>
          {browsable.map((d) => (
            <button key={d.id} type="button"
                    className={`st-tab ${d.id === drive ? 'is-on' : ''}`}
                    disabled={!d.online}
                    title={d.online ? undefined : `${d.label} is offline`}
                    onClick={() => setDrive(d.id)}>{d.label}</button>
          ))}
        </div>
        <input className="input st-all-search" type="search" placeholder="Search every file and folder"
               value={query} onChange={(e) => { setQuery(e.target.value); setShown(PAGE); }}/>
        <label className="st-all-sort">
          <Icon name="sort" size={14}/>
          {/* The headers are the primary control; this is the one that works
              on a phone, where there is no header row to click. */}
          <select className="select" value={`${sort}:${desc ? 'desc' : 'asc'}`}
                  onChange={(e) => {
                    const [k, d] = e.target.value.split(':');
                    setSort(k); setDesc(d === 'desc'); setShown(PAGE);
                  }}
                  aria-label="Sort by">
            {Object.entries(COLUMNS).flatMap(([k, v]) => [
              <option key={`${k}:desc`} value={`${k}:desc`}>
                {v.label} {v.text ? '· Z to A' : '· most first'}
              </option>,
              <option key={`${k}:asc`} value={`${k}:asc`}>
                {v.label} {v.text ? '· A to Z' : '· least first'}
              </option>,
            ])}
          </select>
        </label>
      </div>

      {files && files.length > 0 && (
        <div className="st-all-filters">
          <div className="st-all-chips">
            <button type="button" className={`st-chip ${!kind ? 'is-on' : ''}`}
                    onClick={() => { setKind(''); setShown(PAGE); }}>
              Everything
            </button>
            {Object.entries(KINDS)
              .filter(([k]) => kindCounts[k])
              .map(([k, v]) => (
                <button key={k} type="button" className={`st-chip ${kind === k ? 'is-on' : ''}`}
                        onClick={() => { setKind(kind === k ? '' : k); setShown(PAGE); }}>
                  {v.label} <span className="meta">{kindCounts[k]}</span>
                </button>
              ))}
          </div>

          {/* Only worth showing when more than one person has put anything on
              the drive; otherwise it is a menu with one entry. */}
          {owners.length > 1 && (
            <label className="st-all-owner">
              <Icon name="user" size={13}/>
              <select className="select" value={owner} aria-label="Added by"
                      onChange={(e) => { setOwner(e.target.value); setShown(PAGE); }}>
                <option value="">Anyone</option>
                {owners.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.id === me?.id ? 'you' : o.name} · {fmtBytes(o.bytes)}
                  </option>
                ))}
              </select>
            </label>
          )}

          {filtered && (
            <button type="button" className="btn btn--ghost btn--sm st-all-clear" onClick={reset}>
              Clear filters
            </button>
          )}
        </div>
      )}

      {error && <div className="alert alert--err">{error}</div>}

      {files === null && !error && (
        <div className="stack">
          {[...Array(8)].map((_, i) => <span key={i} className="skeleton st-all-skel"/>)}
        </div>
      )}

      {files && !rows.length && !error && (
        <div className="empty st-empty">
          <Icon name="search" size={22}/>
          <p><strong>{files.length ? 'Nothing matches that.' : (driveRow ? `${driveRow.label} is empty.` : 'Nothing here yet.')}</strong></p>
          {files.length > 0 && (
            <button type="button" className="btn btn--ghost btn--sm" onClick={reset}>Clear filters</button>
          )}
        </div>
      )}

      {rows.length > 0 && (
        <>
          <div className="st-row st-all-row st-all-head">
            <span/>
            {header('name')}
            {header('where')}
            {header('owner')}
            {header('size')}
            {header('added')}
            <span/>
          </div>

          <div className="st-all-list">
            {rows.slice(0, shown).map((f) => (
              <div key={f.id} className={`st-row st-all-row ${f.id === previewId ? 'is-open' : ''}`}
                   aria-current={f.id === previewId ? 'true' : undefined}>
                <Icon name={fileIconName(f.mime_type, f.original_name)} size={15} className="st-icon-file"/>
                <span className="st-all-namecell">
                  {onOpenFile ? (
                    <button type="button" className="st-all-name st-all-open" title={`Open ${f.original_name}`}
                            onClick={() => onOpenFile(f)}>{f.original_name}</button>
                  ) : <span className="st-all-name" title={f.original_name}>{f.original_name}</span>}
                  <MarkerBadge count={f.markers}/>
                </span>
                {/* The folder, not the full path: the file name is already the
                    row, and repeating it doubles the longest column. */}
                <button type="button" className="st-all-where meta" title={`Open ${f.path}`}
                        onClick={() => onOpenFolder(f.drive || drive, f.path || '/')}>
                  {drive === 'all' ? `${label(f.drive)} ${f.path === '/' ? '' : f.path}` : (f.path === '/' ? driveRow?.label : f.path)}
                </button>
                <span className="st-owner meta">{f.owner_id === me?.id ? 'you' : f.owner_name || '—'}</span>
                <span className="st-all-size tnum">{fmtBytes(f.size)}</span>
                <span className="st-date meta">{fmtDate(f.created_at)}</span>
                <span className="st-actions">
                  <button type="button" className="iconbtn" title="Download"
                          aria-label={`Download ${f.original_name}`}
                          onClick={() => onDownload(f)}><Icon name="download" size={15}/></button>
                </span>
              </div>
            ))}
          </div>

          {shown < rows.length && (
            <div className="st-all-more">
              <button type="button" className="btn btn--ghost btn--sm"
                      onClick={() => setShown((n) => n + PAGE * 4)}>
                Show more ({(rows.length - shown).toLocaleString()} left)
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export { parentOf };
